import { runtimeConfig } from "@src/runtime-config";
import { LocalStorageUtils } from "@utils/local-storage-utils";
import { getPathnameFromUrl, getQueryParamsFromSearch } from "@utils/url-utils";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAppParams } from "@router/router-helper";
import { useLoggedInUser } from "@hooks/use-logged-in-user";
import { useStellarApiClient } from "@hooks/use-stellar-api-client";
import { StellarApiTypes } from "@stellar/stellar-api-client";

/**
 * No matter the dependencies it seems that the useEffect to call the init route
 * is called twice, so it stores similar to the isApiInitialized state, whether
 * initialized was already called.
 */
let isApiInitializedLocally = false;

/**
 * Hook to initialize the user in the dashboard 2.0 API
 */
export function useTrackPageLoad(): void {
  const routeLocation = useLocation();
  const appParams = useAppParams();
  const { currentUser } = useLoggedInUser();
  const { stellarApiClient } = useStellarApiClient();

  // Call the routes to initialize the user in the dashboard 2.0 API
  // This route will take care of tracking the page load in Amplitude for all users.
  useEffect(() => {
    async function trackPageLoaded(): Promise<void> {
      const urlPathname = getPathnameFromUrl(routeLocation.pathname, appParams);
      const urlQuery = getQueryParamsFromSearch(routeLocation.search);

      const deviceSizes: StellarApiTypes.SubmitInitDeviceProps = {
        screenWidth: window.screen.width,
        windowWidth: window.innerWidth,
        screenHeight: window.screen.height,
        windowHeight: window.innerHeight,
        windowOrientation: window.screen.orientation?.type,
      };

      try {
        const appInitTime =
          LocalStorageUtils.getStringItem("app-init-time") ?? undefined;
        const response = await stellarApiClient.init.postInit({
          version: runtimeConfig.appVersion,
          application: "Admin Panel",
          userId: currentUser?.id ?? "ADMIN_PANEL_USER",
          userEmail: currentUser?.email,
          appInitTime,
          urlPathname,
          urlQuery,
          ...deviceSizes,
        });
        if (response.success) {
          LocalStorageUtils.setStringItem(
            "app-init-time",
            Date.now().toString()
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console -- It is ok to log the message
        console.error("Error calling init route", { error });
      }
    }

    if (
      !isApiInitializedLocally &&
      routeLocation?.pathname &&
      appParams &&
      currentUser
    ) {
      isApiInitializedLocally = true;
      try {
        // We make sure that there is no way the application fails if something goes wrong in this call
        void trackPageLoaded();
      } catch (error) {
        // eslint-disable-next-line no-console -- It is ok to log the message
        console.error("Error calling init route", { error });
      }
    }
  }, [routeLocation, appParams, currentUser, stellarApiClient]);
}
